/** Session launcher hook — creates/resumes sessions, wires submit and completion, and manages lifecycle. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StudySessionStep, GradingPhaseEvent } from '@formency/shared';
import {
  useActiveSession,
  useCreateLearnSession,
  useAbandonSession,
  useStepSubmit,
  isAlreadyAnswered,
} from '@/shared/api/hooks';
import type { StudySessionWithSteps } from '@/shared/api/hooks/useStudySessions';
import type { SessionSubmitFn } from './SessionPlayer';
import type { StepAnswer } from './types';
import { answerToResponseJson, toUnifiedStepResult } from './sessionUtils';

// ─── Config & Result types ──────────────────────────────────────────────────

export interface SessionLauncherConfig {
  /** Lesson to auto-create a session for. Omit for resume-only mode. */
  lessonId?: string;
  /** Course the lesson belongs to. Omit for personal lessons. */
  courseId?: string;
  /** Guard — disable session fetching/creation until access checks pass. Defaults to true. */
  enabled?: boolean;
  /** Called when the user exits the session (cancel abandon, exit after complete, etc). */
  onExit: () => void;
}

export interface SessionLauncherResult {
  // Session state
  session: StudySessionWithSteps | null;
  sessionReady: boolean;
  loading: boolean;
  createError: boolean;
  sessionCompleted: boolean;
  /** True when resuming an existing active session rather than creating a new one. */
  resuming: boolean;
  // Session player props
  submitFn: SessionSubmitFn;
  sessionType: 'learn' | 'revise';
  startIndex: number;
  sortedSteps: StudySessionStep[];
  confidencePositions: number[];
  paasPositions: number[];
  // Actions
  handleComplete: () => void;
  handleExit: () => void;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useSessionLauncher(config: SessionLauncherConfig): SessionLauncherResult {
  const { lessonId, courseId, enabled = true, onExit } = config;

  const [sessionCompleted, setSessionCompleted] = useState(false);

  // Whether we operate in auto-create mode (lessonId provided) or resume-only mode
  const isAutoCreateMode = !!lessonId;

  // ── Session fetching ────────────────────────────────────────────────────────

  // Track whether we've resolved a session so we can disable polling.
  // Once the session is loaded (either from active query or freshly created),
  // there's no need to keep refetching — the submit endpoint manages all state.
  const sessionLoadedRef = useRef(false);

  // Disable the active session query once completed or once we have a session
  // so background refetches don't clobber local state.
  const {
    data: activeSession,
    isLoading: loadingSession,
    isFetching: fetchingSession,
    isError,
  } = useActiveSession({ enabled: enabled && !sessionCompleted && !sessionLoadedRef.current });

  const createLearnSession = useCreateLearnSession();
  const abandonSession = useAbandonSession();

  // Ref bridges per-call grading phase callbacks to the hook's static callback
  const gradingCallbackRef = useRef<((event: GradingPhaseEvent) => void) | undefined>(undefined);
  const { mutateAsync: submitStep } = useStepSubmit({
    onGradingPhase: (event) => gradingCallbackRef.current?.(event),
  });

  // ── Session resolution ──────────────────────────────────────────────────────

  const sessionNotFound = !loadingSession && !isError && activeSession === null;
  const hasActiveSession = !!activeSession;
  const isForCurrentLesson = hasActiveSession && activeSession.sourceLessonId === lessonId;

  // In resume-only mode (no lessonId), never consider the session "for a different lesson"
  const isForDifferentLesson =
    isAutoCreateMode && hasActiveSession && activeSession.sourceLessonId !== lessonId;

  // ── One-time auto-abandon + auto-create (only in auto-create mode) ─────────
  // The SessionGuardProvider handles the user-facing confirmation dialog before
  // navigation. If we still detect a conflict here (e.g. direct URL access),
  // silently abandon the stale session and create the requested one.
  //
  // NOTE: If we add shareable lesson URLs in the future (e.g. sharing personal
  // lessons with friends), this silent auto-abandon could cause unexpected data
  // loss. Shared lessons should route through a preview/landing page that goes
  // through the guard rather than dropping straight into the session player.

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    if (!isAutoCreateMode || !enabled || loadingSession || !lessonId) return;

    // Session for this lesson already exists — mark as initialized so
    // later cache clears (e.g. on session completion) don't re-create.
    if (isForCurrentLesson) {
      hasInitialized.current = true;
      return;
    }

    // Different lesson's session is active — abandon it first
    if (isForDifferentLesson) {
      hasInitialized.current = true;
      abandonSession.mutate(activeSession!.id, {
        onSuccess: () => {
          createLearnSession.mutate({ lessonId, courseId });
        },
      });
      return;
    }

    // No session at all — create one
    if (sessionNotFound) {
      hasInitialized.current = true;
      createLearnSession.mutate({ lessonId, courseId });
    }
  }, [
    isAutoCreateMode,
    isForCurrentLesson,
    isForDifferentLesson,
    sessionNotFound,
    enabled,
    loadingSession,
    lessonId,
    courseId,
    createLearnSession,
    abandonSession,
    activeSession,
  ]);

  // ── Resolved session ───────────────────────────────────────────────────────

  // In auto-create mode: freshly created session OR the existing active session for this lesson.
  // In resume-only mode: accept whatever active session is found.
  const resolvedSession = isAutoCreateMode
    ? createLearnSession.data || (isForCurrentLesson ? activeSession : null)
    : (activeSession ?? null);

  // Keep a stable reference so completion doesn't unmount SessionPlayer when
  // the active session query data is cleared.
  const [stableSession, setStableSession] = useState(resolvedSession);
  if (resolvedSession && resolvedSession !== stableSession) {
    setStableSession(resolvedSession);
    // Mark as loaded so useActiveSession stops polling
    sessionLoadedRef.current = true;
  }

  // ── Confidence positions ────────────────────────────────────────────────────

  const confidencePositions = useMemo(() => {
    if (!stableSession?.configJson) return [];
    const config = stableSession.configJson as Record<string, unknown>;
    return Array.isArray(config.confidencePositions)
      ? (config.confidencePositions as number[])
      : [];
  }, [stableSession?.configJson]);

  // ── Paas positions ──────────────────────────────────────────────────────────
  // Set at session creation time by the readiness Paas placement (spec §7.2).
  // Empty for cold-start users and for sessions created before the v1
  // readiness work landed (legacy compat — no Paas prompts shown).

  const paasPositions = useMemo(() => {
    if (!stableSession?.configJson) return [];
    const config = stableSession.configJson as Record<string, unknown>;
    return Array.isArray(config.paasPositions) ? (config.paasPositions as number[]) : [];
  }, [stableSession?.configJson]);

  // ── Submit function ─────────────────────────────────────────────────────────

  const submitFn: SessionSubmitFn = useCallback(
    async (
      sid: string,
      position: number,
      answer: StepAnswer,
      onGradingPhase?: (event: GradingPhaseEvent) => void,
      responseTimeMs?: number,
      confidence?: number,
      behavioral?: {
        readingMs?: number;
        answeringMs?: number;
        revisionCount?: number;
        initialResponseJson?: unknown;
        focusLossCount?: number;
        idleCount?: number;
      },
      paasEffort?: number
    ) => {
      gradingCallbackRef.current = onGradingPhase;
      try {
        const isInfoStep = answer.type === 'info';
        const raw = await submitStep({
          sessionId: sid,
          position,
          payload: isInfoStep
            ? {}
            : {
                responseJson: answerToResponseJson(answer),
                responseType: answer.type,
                clientSubmissionId: crypto.randomUUID(),
                clientTs: new Date().toISOString(),
                responseTimeMs,
                confidence,
                ...behavioral,
                ...(paasEffort != null && { paasEffort }),
              },
          isInfoStep,
        });

        // For already-answered responses, return a synthetic result — the caller
        // will handle re-syncing via SYNC_SESSION.
        if (isAlreadyAnswered(raw)) {
          return {
            result: { isCorrect: true, score: 0, maxScore: 0, explanation: null, feedback: null },
            response: null,
            raw,
          };
        }

        return {
          result: toUnifiedStepResult(raw),
          response: raw.response,
          raw,
        };
      } finally {
        gradingCallbackRef.current = undefined;
      }
    },
    [submitStep]
  );

  // ── Event handlers ──────────────────────────────────────────────────────────

  // Exit without abandoning — the session stays resumable. Position is
  // persisted server-side via the step submit endpoint.
  const handleExit = useCallback(() => {
    onExit();
  }, [onExit]);

  // No server call needed — session is auto-completed by the backend when
  // the last step is submitted. We just update local state.
  const handleComplete = useCallback(() => {
    setSessionCompleted(true);
  }, []);

  // ── Loading state ───────────────────────────────────────────────────────────

  // Include the gap between "no session found" and "auto-create in progress"
  const awaitingAutoCreate =
    isAutoCreateMode &&
    sessionNotFound &&
    !createLearnSession.data &&
    !createLearnSession.isError &&
    !stableSession;
  const awaitingAutoAbandon =
    isForDifferentLesson && !createLearnSession.data && !createLearnSession.isError;
  // Wait for fresh data on initial mount — cached data may have stale step states
  // from a previous session load. Once sessionLoadedRef is true, we stop checking.
  const waitingForFreshData = !sessionLoadedRef.current && fetchingSession;
  // Note: createLearnSession.isPending is intentionally NOT included here.
  // awaitingAutoCreate and awaitingAutoAbandon already cover session creation
  // by checking for the absence of data, which is more reliable than the
  // mutation observer's lifecycle state.
  const actuallyLoading =
    loadingSession ||
    waitingForFreshData ||
    abandonSession.isPending ||
    awaitingAutoCreate ||
    awaitingAutoAbandon;

  // Guarantee the loading screen shows long enough for its entrance animation
  // to complete (gradient fill is ~2 s). Timer starts on mount.
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMinTimeElapsed(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const loading = useMemo(
    () => actuallyLoading || !minTimeElapsed,
    [actuallyLoading, minTimeElapsed]
  );

  // ── Derived values ──────────────────────────────────────────────────────────

  const session = stableSession ?? null;
  const sessionReady = !!session && !!session.studySessionSteps?.length;
  const sessionType: 'learn' | 'revise' =
    session?.studySessionType === 'revise' ? 'revise' : 'learn';
  // startIndex is ignored by createInitialState — it derives position from step states.
  // Pass 0 as a placeholder; the reducer handles resume positioning.
  const startIndex = 0;
  const sortedSteps = session?.studySessionSteps
    ? [...session.studySessionSteps].sort((a, b) => a.position - b.position)
    : [];

  // Resuming when the resolved session came from an existing active session, not a fresh create.
  // In auto-create mode: true when we found an active session for this lesson (no create needed).
  // In resume-only mode: true when an active session exists (caller can override via navigation state).
  const resuming = isAutoCreateMode
    ? isForCurrentLesson && !createLearnSession.data
    : hasActiveSession;

  return {
    // Session state
    session,
    sessionReady,
    loading,
    createError: createLearnSession.isError,
    sessionCompleted,
    resuming,
    // Session player props
    submitFn,
    sessionType,
    startIndex,
    sortedSteps,
    confidencePositions,
    paasPositions,
    // Actions
    handleComplete,
    handleExit,
  };
}
